import React from 'react';
import { Briefcase, Sparkles, Rocket } from 'lucide-react';
import MarketingPage, { ContentCard, ContactCallout } from '@/components/marketing/MarketingPage';

const ROLES = [
  { title: 'Founding Engineer (Full-stack)', location: 'Remote', type: 'Full-time',
    desc: 'Own end-to-end features across FastAPI, React and MongoDB. Comfortable with vector search and LLM orchestration.' },
  { title: 'AI/ML Engineer — Matching', location: 'Remote', type: 'Full-time',
    desc: 'Push our vector matching engine forward: embeddings, ranking, bias evaluation, retrieval quality.' },
  { title: 'Product Designer', location: 'Remote', type: 'Full-time',
    desc: 'Shape the Aurora design system and turn complex recruitment flows into effortless experiences.' },
  { title: 'Growth Marketer', location: 'Remote', type: 'Contract',
    desc: 'Own the top of funnel — SEO, content, partnerships. You love writing and shipping campaigns.' },
];

const JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      '@id': 'https://numuv.app/careers#page',
      url: 'https://numuv.app/careers',
      name: 'Careers at Numuv',
      description: 'Open roles at Numuv — a remote-first, product-led team building AI-native recruitment.',
      isPartOf: { '@id': 'https://numuv.app#website' },
    },
    {
      '@type': 'ItemList',
      name: 'Open roles at Numuv',
      itemListElement: ROLES.map((r, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: r.title,
        description: r.desc,
      })),
    },
  ],
};

export default function Careers() {
  return (
    <MarketingPage
      jsonLd={JSONLD}
      seoTitle="Careers at Numuv — join the AI recruitment revolution"
      seoDescription="Open roles at Numuv. We're hiring engineers, designers, and growth marketers to build the future of AI-native recruitment. Remote-first, product-led team."
      seoPath="/careers"
      seoKeywords="Numuv careers, hiring, AI recruitment jobs, remote engineer, product designer jobs"
      eyebrow="We're hiring"
      title="Build the future of hiring with us"
      subtitle="Numuv is small, remote-first, and moving fast. If you obsess over craft and love the messy edge where AI meets people workflows, we should talk."
    >
      <div className="grid gap-4 md:gap-5 mt-4">
        {ROLES.map((r) => (
          <ContentCard key={r.title} className="!p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 t3 text-[12px]"><Briefcase className="w-3.5 h-3.5" /> {r.location} · {r.type}</div>
                <h3 className="dis mt-1.5 text-[18px] md:text-[20px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{r.title}</h3>
                <p className="mt-2 text-[13.5px] md:text-[14px] leading-[21px] md:leading-[22px] t2 max-w-[720px]">{r.desc}</p>
              </div>
              <a
                href={`mailto:hello@numuv.app?subject=${encodeURIComponent(`Application — ${r.title}`)}`}
                className="rounded-full aurora-btn px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-[0_10px_24px_rgba(109,94,247,.32)] hover:brightness-105 transition inline-flex items-center gap-2"
                data-testid={`apply-${r.title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
              >
                <Rocket className="w-3.5 h-3.5" /> Apply
              </a>
            </div>
          </ContentCard>
        ))}
      </div>

      <ContentCard className="mt-8">
        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">
          <Sparkles className="w-4 h-4" /> Don&apos;t see the right role?
        </div>
        <p className="mt-3 text-[14.5px] t2">
          We&apos;re always open to exceptional people. Send a note about what you&apos;d want to work on and how you&apos;d help Numuv grow.
        </p>
        <div className="mt-4"><ContactCallout /></div>
      </ContentCard>
    </MarketingPage>
  );
}
